import {
  CheckboxVisibility,
  CollapseAllVisibility,
  ConstrainMode,
  DetailsList,
  DetailsRow,
  HoverCard,
  HoverCardType,
  IColumn,
  Icon,
  IconButton,
  IDetailsHeaderProps,
  IDetailsRowProps,
  IGroup,
  IGroupDividerProps,
  IRenderFunction,
  MessageBar,
  MessageBarType,
  ScrollablePane,
  Selection,
  Sticky,
  StickyPositionType,
  TooltipHost,
} from '@fluentui/react';
import * as _ from 'lodash';
import moment from 'moment-timezone';
import * as React from 'react';
import { Market } from '../../api/markets.types';
import { ProviderData } from '../../api/providers.types';
import { AsyncData } from '../../redux/core';
import { Break, ProviderSchedules } from '../../redux/providers/types';
import { ConfirmationStateType, Visit } from '../../redux/visits/types';
import { css } from '../../util/Css';
import { friendlyAge } from '../../util/Date';
import { BreakDialog } from './dialogs/BreakDialog';
import { EtaScoreBreakdown } from './EtaScoreBreakdown';
import patientConfirmationStyles from './PatientConfirmation.module.scss';
import './VisitDetailsList.scss';
import { VisitDetailsListHeader } from './VisitDetailsListHeader';
import { VisitStatusIcon } from './VisitStatusIcon';
import { VisitTag } from './VisitTag';

export interface VisitDetailsListProps {
  items: any[];
  markets: Market[];
  visitCode?: string;
  date: string;
  marketToProviderSchedules: { [marketCode: string]: AsyncData<ProviderSchedules[]> };
  allProviders: AsyncData<ProviderData[]>;
  actions: {
    setVisitCode: (code: string) => void;
    editBreak: (id: string, doctorId: string, startTime: number, endTime: number, type: string, MAId?: string) => void;
    deleteBreak: (breakId: string) => void;
  };
}

export interface VisitDetailsListState {
  sortedItems: (Visit | Break)[];
  groups?: IGroup[];
  visitColumns: IColumn[];
  height: number;
}

export class VisitDetailsList extends React.Component<VisitDetailsListProps, VisitDetailsListState> {
  private container: HTMLDivElement | null = null;
  private selection: Selection = new Selection({
    getKey: (data: any) => data.visitCode,
    canSelectItem: (data: any) => !!data.visitCode,
  });

  constructor(props: VisitDetailsListProps) {
    super(props);

    this.state = {
      sortedItems: [],
      visitColumns: this.buildVisitColumns(),
      height: 0,
    };
  }

  public componentDidMount() {
    this.setItems(this.props.items, this.props.markets);
    this.resizeVisitsListHeight();
    window.addEventListener('resize', this.resizeVisitsListHeight);
  }

  private resizeVisitsListHeight = () => {
    if (this.container) {
      const scrollableVisitsListPaneHeight = window.innerHeight - 32 - this.container.getBoundingClientRect().top;
      this.setState({ height: scrollableVisitsListPaneHeight });
    }
  };

  public componentDidUpdate(prevProps: VisitDetailsListProps) {
    if (this.props.items !== prevProps.items || this.props.markets !== prevProps.markets) {
      this.setItems(this.props.items, this.props.markets);
      if (this.props.visitCode) {
        this.selection.setKeySelected(this.props.visitCode, true, true);
      }
    }

    if (this.state.height === 0) {
      this.resizeVisitsListHeight();
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.resizeVisitsListHeight);
  }

  public render(): JSX.Element {
    const { sortedItems, visitColumns, groups } = this.state;

    if (this.props.markets.length === 0) {
      return (
        <div style={{ marginTop: 20 }}>
          <MessageBar messageBarType={MessageBarType.info}>Please select the market(s) to view.</MessageBar>
        </div>
      );
    }

    return (
      <div ref={(c) => (this.container = c)} style={{ position: 'relative', height: this.state.height }}>
        <ScrollablePane>
          <DetailsList
            constrainMode={ConstrainMode.horizontalConstrained}
            checkboxVisibility={CheckboxVisibility.hidden}
            items={sortedItems}
            groups={groups}
            selection={this.selection}
            groupProps={{
              collapseAllVisibility: CollapseAllVisibility.hidden,
              onRenderHeader: this.onRenderHeader,
              showEmptyGroups: true,
              onRenderFooter: this.onRenderFooter,
            }}
            setKey="set"
            columns={visitColumns}
            onRenderRow={this.onRenderRow}
            onShouldVirtualize={() => false}
            onRenderDetailsHeader={
              // tslint:disable-next-line:jsx-no-lambda
              (detailsHeaderProps?: IDetailsHeaderProps, defaultRender?: IRenderFunction<IDetailsHeaderProps | undefined>) => (
                <Sticky stickyPosition={StickyPositionType.Header}>{!!defaultRender && defaultRender(detailsHeaderProps)}</Sticky>
              )
            }
          />
        </ScrollablePane>
      </div>
    );
  }

  private onRenderRow = (props?: IDetailsRowProps): JSX.Element => {
    props = props!;
    if (props.item.visitCode) {
      return this.renderVisitRow(props.item as Visit, props);
    } else {
      return this.renderBreakRow(props);
    }
  };

  private renderBreakRow(props: IDetailsRowProps) {
    return <DetailsRow className="break-row" {...props} />;
  }

  private renderVisitRow(visit: Visit, props: IDetailsRowProps) {
    const rowClass = css({
      'visit-behind-schedule': visit.recommendedTime > visit.timeslotEndTime,
      'visit-address-duplicate': (visit as any).duplicateAddress,
    });
    return (
      /* 
      [OPS-2453] On closing side panel last active row is focused again triggering onActiveItemChanged to fire.
      If DetailsList or DetailsRow supports onClick prop in the future use that instead of this hack.
      https://github.com/microsoft/fluentui/issues/14659
      */

      <div onClick={() => this.props.actions.setVisitCode(visit.visitCode)}>
        <DetailsRow className={rowClass} {...props} />
      </div>
    );
  }

  private onRenderFooter = (): JSX.Element => {
    return <div style={{ marginTop: 60 }} />;
  };

  private onRenderHeader = (props?: IGroupDividerProps): JSX.Element => {
    const group = props!.group!;
    const visitsCount = this.state.sortedItems!.slice(group.startIndex, group.startIndex + group.count).filter((v) => !!(v as Visit).visitCode).length;
    const breakCount = this.state.sortedItems!.slice(group.startIndex, group.startIndex + group.count).filter((v) => !(v as Visit).visitCode).length;
    return <VisitDetailsListHeader market={this.props.markets.find((m) => m.code === group.key)} visitCount={visitsCount} breakCount={breakCount} collapsed={group.isCollapsed!} />;
  };

  private setItems(visits: any[], markets: Market[]) {
    const marketToVisitsMap: { [code: string]: any[] } = {};
    const marketCodes = markets.map((m) => m.code);

    for (let i = 0; i < marketCodes.length; i++) {
      marketToVisitsMap[marketCodes[i]] = [];
    }

    for (let i = 0; i < visits.length; i++) {
      if (marketToVisitsMap[visits[i].marketCode]) {
        marketToVisitsMap[visits[i].marketCode].push(visits[i]);
      }
    }

    // TODO: this needs to go.
    for (let i = 0; i < marketCodes.length; i++) {
      marketToVisitsMap[marketCodes[i]].sort((a, b) => a.scheduledTime - b.scheduledTime);
    }

    const groups: IGroup[] = [];
    let startIndex = 0;
    const sortedGroupedVisits = _.flatten(
      marketCodes.map((m) => {
        groups.push({
          key: m,
          name: m,

          startIndex: startIndex,
          count: marketToVisitsMap[m].length,
        });
        startIndex += marketToVisitsMap[m].length;
        return marketToVisitsMap[m];
      })
    );

    this.selection.setItems(sortedGroupedVisits);
    this.setState({ sortedItems: sortedGroupedVisits, groups: groups });
  }

  private buildVisitColumns(): IColumn[] {
    const columns: IColumn[] = [
      {
        key: 'insurance',
        name: '',
        fieldName: 'insurance',
        minWidth: 60,
        maxWidth: 60,
        onRender: (item: Visit) => {
          if (item.visitCode) {
            return <VisitTag visit={item} />;
          }
          return null;
        },
      },
      {
        key: 'status',
        name: 'Status',
        fieldName: 'status',
        minWidth: 70,
        maxWidth: 70,
        isMultiline: true,
        onRender: (item: Visit) => {
          if (item.visitCode) {
            return <VisitStatusIcon status={item.status || item.visitStatus} />;
          } else {
            if ((item as any).type === 'Labs') {
              return (
                <div style={{ textAlign: 'center' }}>
                  <i className={`ms-font-xl ms-Icon ms-Icon--TestBeakerSolid`} style={{ color: '#2c78d4b5' }} />
                  <div style={{ color: '#2c78d4b5', fontWeight: 600, fontSize: 10 }}>{(item as any).type}</div>
                </div>
              );
            } else {
              return (
                <div style={{ textAlign: 'center' }}>
                  <i className={`ms-font-xl ms-Icon ms-Icon--CoffeeScript`} style={{ color: '#2c78d4b5' }} />
                  <div style={{ color: '#2c78d4b5', fontWeight: 600, fontSize: 10 }}>{(item as any).type}</div>
                </div>
              );
            }
          }
        },
      },
      {
        key: 'visitcode',
        name: 'Visit Code',
        fieldName: 'visitCode',
        minWidth: 70,
        maxWidth: 70,
      },
      {
        key: 'service',
        name: 'Service',
        fieldName: 'visitServiceShortName',
        minWidth: 80,
        maxWidth: 80,
        isMultiline: true,
        onRender: (item: Visit) => {
          if (item.visitCode) {
            if (item.visitServiceCode === 'ANPS_ADULT' || item.visitServiceCode === 'ANPS_CHILD') {
              return <div style={{ fontWeight: 'bold' }}>{item.visitServiceShortName}</div>;
            }
            return <div>{item.visitServiceShortName}</div>;
          }
          return null;
        },
      },
      {
        key: 'patient',
        name: 'Patient',
        fieldName: 'buildPatientFullName',
        minWidth: 80,
        maxWidth: 140,
        isMultiline: true,
        onRender: (item: Visit) => {
          if (item.visitCode) {
            return (
              <div>
                <div>{item.buildPatientFullName}</div>
                <div style={{ fontWeight: 'bold' }}>{friendlyAge(item.patientDob)}</div>
              </div>
            );
          }
          return null;
        },
      },
      {
        key: 'address',
        name: 'Address',
        fieldName: 'addressLong',
        minWidth: 120,
        maxWidth: 240,
        isMultiline: true,
      },
      {
        key: 'doctor',
        name: 'Provider',
        fieldName: '',
        minWidth: 80,
        maxWidth: 140,
        isMultiline: true,
        onRender: (item: Visit) => {
          let name = item.buildDoctorFullName;
          if (!name && item.recommendedDoctor && this.props.allProviders.data) {
            name = (this.props.allProviders.data.filter((p) => p.id === item.recommendedDoctor)[0] || {}).fullName;
          }
          if (!name) {
            const isToday = moment(item.timeslotStartTime).isSame(new Date(), 'days');
            if (item.visitCode && isToday) {
              return (
                <TooltipHost content="Auto routing cannot find a doctor due to break validation failing or missing doctor schedule.">
                  <i className={`ms-font-xl ms-Icon ms-Icon--Error`} style={{ color: '#FF0000' }} />
                </TooltipHost>
              );
            }
            return null;
          }

          const estimated = !!item.visitCode && !item.buildDoctorFullName && !!item.recommendedDoctor;

          if (item.visitCode) {
            const plainCardProps = {
              onRenderPlainCard: () => <EtaScoreBreakdown visitCode={item.visitCode} />,
            };
            return (
              <HoverCard plainCardProps={plainCardProps} type={HoverCardType.plain}>
                <div className={css('doctor-status', { estimated: estimated })}>
                  <div className="text">{name}</div>
                </div>
              </HoverCard>
            );
          }

          return (
            <div className={css('doctor-status', { estimated: estimated })}>
              <div className="text">{name}</div>
            </div>
          );
        },
      },
      {
        key: 'assistant',
        name: 'Assistant',
        fieldName: 'buildAssistantFullName',
        minWidth: 80,
        maxWidth: 140,
        isMultiline: true,
        onRender: (item: Visit) => {
          if (item.visitCode) {
            return item.buildAssistantFullName;
          } else {
            if (this.props.allProviders.isFetching || !this.props.allProviders.data) {
              return item.assistantId;
            }
            const provider = this.props.allProviders.data.find((p) => p.id === item.assistantId);
            if (!provider) {
              return item.assistantId;
            }
            return provider.prefix + ' ' + provider.firstName + ' ' + provider.lastName + (provider.suffix && ', ' + provider.suffix);
          }
        },
      },
      {
        key: 'timeslotStartTime',
        name: 'Timeslot',
        fieldName: '',
        minWidth: 100,
        maxWidth: 100,
        onRender: (item: Visit) => {
          const timeZone = item.timezone ?? this.props.markets.find((m) => m.code === item.marketCode)?.timeZone;
          if (!timeZone) {
            return null;
          }

          if (item.visitCode) {
            // rendering a visit
            const mtzStart = moment(item.timeslotStartTime).tz(timeZone);
            const mtzEnd = moment(item.timeslotEndTime).tz(timeZone);
            return (
              <div style={{ textAlign: 'center' }}>
                <div>{mtzStart.format('ddd MMM D YYYY')}</div>
                <div>
                  {mtzStart.format('hA')} - {mtzEnd.format('hA')}{' '}
                </div>
              </div>
            );
          } else {
            // rendering a break
            const mtzStart = moment((item as any).startTime).tz(timeZone);

            return (
              <div style={{ textAlign: 'center' }}>
                <div>{mtzStart.format('ddd MMM D YYYY')}</div>
                <div>{((item as any).endTime - (item as any).startTime) / 60000} minutes</div>
              </div>
            );
          }
        },
      },
      {
        key: 'scheduledTime',
        name: 'Scheduled',
        fieldName: '',
        minWidth: 80,
        maxWidth: 80,
        onRender: (item: Visit) => {
          const timeZone = item.timezone ?? this.props.markets.find((m) => m.code === item.marketCode)?.timeZone;
          if (!timeZone) {
            return null;
          }

          const mtz = moment(item.scheduledTime).tz(timeZone);
          const format = moment.tz.guess() === timeZone ? 'h:mm A' : 'h:mm A z';

          return (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold' }}>{mtz.format(format)}</div>
            </div>
          );
        },
      },
      {
        key: 'eta',
        name: 'ETA',
        fieldName: '',
        minWidth: 75,
        maxWidth: 75,
        onRender: (item: Visit) => {
          if (item.visitCode) {
            const timeZone = item.timezone ?? this.props.markets.find((m) => m.code === item.marketCode)?.timeZone;
            if (!timeZone) {
              return null;
            }
            const format = moment.tz.guess() === timeZone ? 'h:mm A' : 'h:mm A z';

            let time = item.timeProviderExpectedArrival;
            let estimated = false;
            if (time === -1) {
              time = item.recommendedTime;
              estimated = true;
            }

            if (time !== -1) {
              const mtz = moment(time).tz(timeZone);
              return (
                <div className={css('eta-status', { estimated: estimated })}>
                  <div className="text">{mtz.format(format)}</div>
                </div>
              );
            }
            return null;
          } else {
            return (
              <IconButton
                iconProps={{ iconName: 'Edit' }}
                title="Edit break"
                ariaLabel="Edit break"
                onClick={() =>
                  BreakDialog.show({
                    isEdit: true,
                    breakInfo: item as any,
                    markets: this.props.markets,
                    marketToProviderSchedules: this.props.marketToProviderSchedules,
                    editBreak: this.props.actions.editBreak,
                    deleteBreak: this.props.actions.deleteBreak,
                    uiDate: this.props.date,
                  })
                }
              />
            );
          }
        },
      },
      {
        key: 'duration',
        name: 'Duration',
        minWidth: 60,
        maxWidth: 60,
        onRender: (item: Visit) => {
          if (!item.visitCode || item.timeStartedVisit < 0) {
            return null;
          }

          const timeZone = item.timezone ?? this.props.markets.find((m) => m.code === item.marketCode)?.timeZone;
          if (!timeZone) {
            return null;
          }

          const mtzStart = moment(item.timeStartedVisit).tz(timeZone);
          const mtzEnd = item.timeEndedVisit > 0 ? moment(item.timeEndedVisit).tz(timeZone) : moment().tz(timeZone);
          return <div style={{ textAlign: 'center' }}>{`${moment.duration(mtzEnd.diff(mtzStart)).asMinutes().toFixed(0)} mins`}</div>;
        },
      },
      {
        key: 'reschedules',
        name: 'Reschedules',
        fieldName: 'reschedules',
        minWidth: 30,
        maxWidth: 30,
        onRenderHeader: () => (
          <TooltipHost id="reschedules" content="How many times a visit has been rescheduled.">
            <Icon iconName="Rotate90Clockwise" />
          </TooltipHost>
        ),
      },
      {
        key: 'confirmation',
        name: 'Confirmation',
        minWidth: 100,
        maxWidth: 100,
        isMultiline: true,
        onRender: (item: Visit) => {
          if (!item?.confirmationState) return null;

          return (
            <div className={css(patientConfirmationStyles.confirmationState, patientConfirmationStyles[item.confirmationState])}>
              {item.confirmationState === ConfirmationStateType.CONFIRMED_MANUAL ? 'OPS CONFIRMED' : item.confirmationState.replaceAll('_', ' ')}
            </div>
          );
        },
      },
    ];

    return columns;
  }
}
